import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * Guest opens link from email: /payfast-checkout?token=...
 * Loads signed PayFast fields via callable and auto-submits POST to PayFast.
 */
export default function PayFastCheckout() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!token) {
      setError('Missing payment link. Use the link from your email.');
      return;
    }

    const run = async () => {
      try {
        const getData = httpsCallable(functions, 'getPayFastCheckoutData');
        const result = await getData({ token });
        const data = result.data as { actionUrl: string; fields: Record<string, string> };
        if (!data.actionUrl || !data.fields) {
          setError('Invalid payment session.');
          return;
        }
        const form = formRef.current;
        if (!form) return;
        form.action = data.actionUrl;
        form.method = 'POST';
        // Clear and add hidden inputs
        form.innerHTML = '';
        Object.entries(data.fields).forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        });
        form.submit();
      } catch (e: any) {
        setError(e.message || 'Could not start payment. Link may have expired.');
      }
    };
    run();
  }, [token]);

  if (error) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <h1 className="text-xl font-bold mb-4">Payment</h1>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c] mx-auto mb-4" />
      <p className="text-gray-600">Redirecting to PayFast…</p>
      <form ref={formRef} style={{ display: 'none' }} />
    </div>
  );
}
